import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input, OnChanges,
  OnInit, SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { McRAIImage } from '../../models/image';
import { MediaComponent } from '../media/media.component';

@Component({
  selector: 'mcrai-media[type=image]',
  template: `<ng-container *ngIf="isLoading">
  <mcrai-loader></mcrai-loader>
</ng-container>

<ng-container *ngIf="isLoaded">
  <img class="mcrai-media__image" [src]="src" alt="">
</ng-container>

<button
  *ngIf="showCloseButton"
  class="mcrai-media__control mcrai-media__control--close"
  (click)="close()"
  mcrai-icon-button="negative"
>
  <mcrai-icon class="mcrai-media__control-icon" [color]="controlColor">
    close
  </mcrai-icon>
</button>

<ng-container *ngIf="isGallery">
  <button
    class="mcrai-media__control mcrai-media__control--move mcrai-media__control--prev"
    *ngIf="!isFirst"
    (click)="prev($event)"
    mcrai-icon-button="neutral"
  >
    <mcrai-icon class="mcrai-media__control-icon" [color]="controlColor">
      arrow_left
    </mcrai-icon>
  </button>
  <button
    class="mcrai-media__control mcrai-media__control--move mcrai-media__control--next"
    *ngIf="!isLast"
    (click)="next($event)"
    mcrai-icon-button="neutral"
  >
    <mcrai-icon class="mcrai-media__control-icon" [color]="controlColor">
      arrow_right
    </mcrai-icon>
  </button>
</ng-container>
`,
  styles: [`.mcrai-media[type=image]{display:flex;align-items:center;justify-content:center;position:relative}.mcrai-media__image{max-width:100%;max-height:100%;-o-object-fit:contain;object-fit:contain;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.mcrai-media__control{position:absolute;width:50px;height:50px}.mcrai-media__control--close{right:0;top:0}.mcrai-media__control--move{top:0;bottom:0;margin:auto!important}.mcrai-media__control--prev{left:0}.mcrai-media__control--next{right:0}.mcrai-media__control-icon{font-size:27px!important}`],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  host: {
    '[class.mcrai-media]': 'true',
    '[class.mcrai-media--image]': 'true'
  }
})
export class ImageComponent extends MediaComponent implements OnInit, OnChanges, AfterViewInit {
  private _images: McRAIImage[] = [];

  private _activeIndex = 0;
  set activeIndex(value: number) {
    this._activeIndex = value;
  }
  get activeIndex(): number {
    return this._activeIndex;
  }

  @Input() set data(value: McRAIImage | McRAIImage[]) {
    if (value) {
      this._images = Array.isArray(value) ? value : [value];
    }
  }

  private _isLoading = true;
  get isLoading(): boolean {
    return this._isLoading;
  }

  get isLoaded(): boolean {
    return !this._isLoading && coerceBooleanProperty(this._src);
  }

  get isGallery(): boolean {
    return this._images.length > 1;
  }

  private _showCloseButton = false;
  set showCloseButton(value: boolean) {
    this._showCloseButton = value;
  }
  get showCloseButton(): boolean {
    return this._showCloseButton;
  }

  get isFirst(): boolean {
    return this._activeIndex === 0;
  }

  get isLast(): boolean {
    return this._activeIndex === this._images.length - 1;
  }

  private _controlColor = 'primary';
  @Input() set controlColor(value: string) {
    this._controlColor = value;
  }
  get controlColor(): string {
    return this._controlColor;
  }

  ngOnInit() { }

  ngAfterViewInit() {
    this._loadImage();
  }

  ngOnChanges(changes: SimpleChanges) {
    // every time new image comes, update it, but skip first one (ngAfterViewInit handles first image)
    if (changes.hasOwnProperty('data')) {
      if (!changes.data.firstChange && this._shouldLoadImage(changes.data.previousValue, changes.data.currentValue)) {
        this._loadImage();
      } else {
        this._renderer.setStyle(this._elRef.nativeElement, 'width', this.toString(this._width));
        this._renderer.setStyle(this._elRef.nativeElement, 'height', this.toString(this._height));
        this._onLoadEnd();
      }
    }
  }

  private _shouldLoadImage(prev: McRAIImage | McRAIImage[], curr: McRAIImage | McRAIImage[]): boolean {
    if (!curr) {
      return false;
    }

    if (!prev) {
      return true;
    }

    if (Array.isArray(prev) && Array.isArray(curr)) {
      for (let i = 0; i < prev.length; i++) {
        if (prev[i].path !== curr[i].path) {
          return true;
        }
      }
      return false;
    }

    if (prev.hasOwnProperty('path') && curr.hasOwnProperty('path')) {
      return (<McRAIImage>prev).path !== (<McRAIImage>curr).path;
    }

    return false;
  }

  private _loadImage(): void {
    this._ngZone.runOutsideAngular(() => {
      if (this._images.length) {
        this._onLoadStart(this._images[this._activeIndex]);
      } else {
        this._onLoadEnd();
      }
    });
  }

  private _setDimensions(image: McRAIImage): void {
    const parent = this._elRef.nativeElement.getBoundingClientRect();

    let tmp, width, height;

    switch (true) {
      case coerceBooleanProperty(this._width) && coerceBooleanProperty(this._height):
        width  = this.toString(this._width);
        height = this.toString(this._height);
        break;

      case this._width === '100%' && !coerceBooleanProperty(this._height):
        width = this.toString(this._width);
        height = 'auto';
        break;

      case coerceBooleanProperty(this._width) && !coerceBooleanProperty(this._height):
        width  = this.toString(this._width);
        tmp    = this._calculateDimension(image.height, image.width, this._width, parent.width);
        height = this.toString(tmp);
        break;

      case !coerceBooleanProperty(this._width) && coerceBooleanProperty(this._height):
        height = this.toString(this._height);
        tmp    = this._calculateDimension(image.width, image.height, this._height, parent.height);
        width  = this.toString(tmp);
        break;

      case !coerceBooleanProperty(this._width) && !coerceBooleanProperty(this._height):
      default:
        width  = parent.width;
        tmp    = this._calculateDimension(image.height, image.width, this._width, parent.width);
        height = this.toString(tmp);
        break;
    }

    this._renderer.setStyle(this._elRef.nativeElement, 'width', width);
    this._renderer.setStyle(this._elRef.nativeElement, 'height', height);
  }

  private _calculateDimension(
    origDim: number | string, origRefDim: number | string, newRefDim: number | string, parentDim: number): number {

    return this.toNumber(origRefDim, parentDim)
      / this.toNumber(newRefDim, parentDim)
      * this.toNumber(origDim, parentDim);
  }

  private _onLoadStart(image: McRAIImage): void {
    this._isLoading = true;
    this._setDimensions(image);
    this._changeDetectorRef.detectChanges();

    const tmpImage = new Image();

    tmpImage.onload = () => {
      this._src = image.path;
      this._onLoadEnd();
    };

    tmpImage.src = image.path;
  }

  private _onLoadEnd(): void {
    this._isLoading = false;

    if (this._changeDetectorRef && !this._changeDetectorRef['destroyed']) {
      this._changeDetectorRef.detectChanges();
    }
  }

  private _decrementIndex(): void {
    // this._activeIndex = this._activeIndex - 1 < 0 ? this._images.length - 1 : this._activeIndex - 1;
    this._activeIndex--;
  }

  private _incrementIndex(): void {
    // this._activeIndex = this._activeIndex + 1 >= this._images.length ? 0 : this._activeIndex + 1;
    this._activeIndex++;
  }

  public prev(event): void {
    event.stopPropagation();

    this._decrementIndex();
    this._loadImage();
  }

  public next(event): void {
    event.stopPropagation();

    this._incrementIndex();
    this._loadImage();
  }
}
